import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Home as HomeIcon } from "lucide-react";
import { MaskedLines, EASE, Reveal } from "@/lib/motion";
import { useBooking } from "@/lib/booking";
import { SEO } from "@/components/site/SEO";

const RECOVERY = [
  { name: "Home", desc: "Start from the top — the whole growth engine, on one page.", to: "/" },
  { name: "Services", desc: "Strategy, blog, conversion assets, and operations.", to: "/services" },
  { name: "Case Studies", desc: "Real numbers from Capillary, Airmeet, inFeedo, JustCall.", to: "/case-studies" },
  { name: "How We Work", desc: "The Dandelion Method — six steps that compound.", to: "/how-we-work" },
];

export default function NotFound() {
  const { pathname } = useLocation();
  const { openBooking } = useBooking();

  return (
    <div data-testid="not-found-page" className="bg-white">
      <SEO
        title="Page Not Found"
        description="The page you're looking for has moved or never existed. Head back home, or start a conversation about your content acquisition engine."
        path={pathname}
        noindex
      />

      <section className="pt-40 pb-16 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#999999] mb-8">Error · 404</p>
          <h1 className="font-serif text-[18vw] sm:text-[10rem] lg:text-[14rem] leading-[0.9] tracking-tight text-[#111111]">
            <MaskedLines lines={["404."]} delay={0.05} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className="mt-8 max-w-2xl font-serif italic text-2xl md:text-3xl text-[#666666] leading-tight"
          >
            This page didn&apos;t rank. Even for us.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-[#666666] leading-relaxed"
          >
            The URL <code className="font-mono text-[#111111] bg-[#F5F5F5] px-2 py-1">{pathname}</code>{" "}
            doesn&apos;t exist here — the page has moved, been retired, or you just took a wrong turn.
            Try one of the routes below, or start a conversation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/" data-testid="nf-home" className="inline-flex items-center gap-2 bg-[#111111] text-white font-semibold px-8 py-4 hover:bg-[#333333] transition-colors group">
              <HomeIcon size={17} /> Back Home
            </Link>
            <button type="button" onClick={openBooking} data-testid="nf-audit" className="inline-flex items-center gap-2 border border-[#111111] text-[#111111] font-semibold px-8 py-4 hover:bg-[#F5F5F5] transition-colors group">
              Schedule a Content Audit
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Recovery routes */}
      <section className="section-pad bg-[#F5F5F5]" aria-labelledby="try-instead">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#999999] mb-6 font-mono">Try one of these instead</p>
            <h2 id="try-instead" className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-[#111111]">
              Four pages that will actually help.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#E0E0E0]">
            {RECOVERY.map((r, i) => (
              <Reveal key={r.to} delay={i * 0.06}>
                <Link
                  to={r.to}
                  data-testid={`nf-link-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative block h-full p-8 md:p-10 border-r border-b border-[#E0E0E0] bg-[#F5F5F5] hover:bg-[#111111] transition-colors duration-300"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-[#999999] group-hover:text-[#666666]">0{i + 1}</span>
                    <ArrowUpRight size={22} className="text-[#999999] group-hover:text-white transition-all group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <h3 className="mt-8 font-serif text-3xl md:text-4xl text-[#111111] group-hover:text-white transition-colors">
                    {r.name}
                  </h3>
                  <p className="mt-4 text-[#666666] group-hover:text-[#E0E0E0] transition-colors leading-relaxed">
                    {r.desc}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
